'use client';
import React, { Suspense, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { customAxios } from '@/modules/common/api';
import { API } from '@/app/api/config';
import SweetAlert from 'sweetalert2';
import { hashPassword } from '@/modules/common/password';
import { isValidEmail } from '@/modules/common/email';
import { useAuth } from '@/modules/auth/AuthContext';

interface LoginParam {
  acPhone?: string;
  acPw?: string;
}

export default function SocialLoginPage() {
  return (
    <Suspense fallback={null}>
      <SocialLoginPageContent />
    </Suspense>
  );
}

function SocialLoginPageContent() {
  const { isLoggedIn, isLoading } = useAuth();
  const passwordRegEx = /^(?=.*[a-zA-Z])(?=.*[!@#$%^*+=-])(?=.*[0-9]).{8,15}$/;

  const [isEmailCheck, setIsEmailCheck] = useState<Boolean>();
  const [isPasswordCheck, setIsPassWordCheck] = useState<Boolean>();
  const [seePassword, setSeePassword] = useState(false);

  const Rest_api_key = process.env.NEXT_PUBLIC_KAKAO_RESTAPI;
  const redirect_uri = process.env.NEXT_PUBLIC_KAKAO_REDIRECT_URI;

  // oauth 요청 URL
  const kakaoURL = `https://kauth.kakao.com/oauth/authorize?client_id=${Rest_api_key}&redirect_uri=${redirect_uri}&response_type=code&scope=birthday,birthyear,gender`;
  const searchParams = useSearchParams();

  // const url = new URL(window.location.href);
  // const code = url.searchParams.get("code");
  const code = searchParams.get('code');
  const access_token = searchParams.get('access_token');

  const [loginParam, setLoginParam] = useState<LoginParam>({});

  const onChangeLoginParam = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginParam({ ...loginParam, [name]: value });
  };

  const handleLogin = () => {
    // window.location.href = kakaoURL;
    window.open(kakaoURL, '_self');
  };

  //로그인 기능
  const onClickLogin = async () => {
    if (!loginParam.acPhone) {
      SweetAlert.fire({
        text: '아이디를 입력해주세요.',
        icon: 'warning',
        showConfirmButton: true,
      }).then((result) => {
        if (result.isConfirmed) return;
      });
      return;
    } else if (!loginParam.acPw) {
      SweetAlert.fire({
        text: '비밀번호를 입력해주세요.',
        icon: 'warning',
        showConfirmButton: true,
      }).then((result) => {
        if (result.isConfirmed) return;
      });
      return;
    }

    if (!isEmailCheck) {
      SweetAlert.fire({
        text: '아이디가 이메일 형식에 맞지 않습니다.',
        icon: 'warning',
        showConfirmButton: true,
      }).then((result) => {});
      return;
    }

    if (loginParam != null) {
      await customAxios
        .post(`${API.LOGIN}`, { ...loginParam, acPw: hashPassword(loginParam.acPw || '') })
        .then((res) => {
          if (res?.status === 200) {
            // 로그인 성공 시 백엔드가 httpOnly 쿠키로 토큰을 내려준다.
            location.href = '/';
          }
        })
        .catch(() => {});
    }
  };

  // 엔터 키 이벤트 처리 핸들러 추가
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // 기본 폼 제출 동작 방지
      onClickLogin();
    }
  };

  const emailCheck = (username: string) => {
    setIsEmailCheck(isValidEmail(username));
  };
  const passwordCheck = (password: string) => {
    if (password.match(passwordRegEx) === null) {
      setIsPassWordCheck(false);
    } else {
      setIsPassWordCheck(true);
    }
  };

  useEffect(() => {
    if (!code && !isLoading && isLoggedIn) {
      location.href = '/';
    }
  }, [code, isLoading, isLoggedIn]);

  useEffect(() => {
    if (code) {
      // 로그인 성공 시 백엔드가 httpOnly 쿠키로 토큰을 내려준다.
      customAxios.get(`${API.USER_WEB}/login/kakao?code=${access_token}`).then(() => {
        location.href = '/';
      });
    }
  }, []);

  return (
    <div>
      <div className='max-w-[1360px] m-auto mt-[100px] mb-[300px] mobile:w-full mobile:px-[40px]'>
        <div className='sec-login'>
          <div className='wrap-login mobile:w-full'>
            <div className='memu_title'>LOGIN</div>

            <form onSubmit={(e) => { e.preventDefault(); onClickLogin(); }}>
              <div className='mb-[10px] min-w-[520px] mobile:w-full mobile:min-w-full'>
                <div className='mb-[14px]'>
                  <input
                    className='w-full h-[64px] resize px-[10px] py-[12px] border border-solid border-[#909090] rounded-[5px] box-border'
                    name='acPhone'
                    type='text'
                    placeholder='아이디(이메일)을 입력하세요.'
                    onChange={(e) => {
                      onChangeLoginParam(e);
                      emailCheck(e.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                  />
                  <p style={{ color: 'red', textAlign: 'left' }}>
                    {isEmailCheck == false ? '이메일 형식에 맞게 입력해주세요.' : ''}
                  </p>
                </div>
                <div className={'relative'}>
                  <input
                    className='input-basic'
                    name='acPw'
                    type={seePassword ? 'text' : 'password'}
                    placeholder='비밀번호 입력'
                    onChange={(e) => {
                      onChangeLoginParam(e);
                      passwordCheck(e.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                  />
                  <div
                    className={'absolute top-[22%] right-[17px] w-[30px] cursor-pointer'}
                    onClick={() => setSeePassword(!seePassword)}
                  >
                    {seePassword ? (
                      <img src={'/eye-icon.png'} />
                    ) : (
                      <img src={'/closed-eyes-icon.png'} />
                    )}
                  </div>
                </div>
              </div>
            </form>

            <div className='flex items-center justify-center gap-[10px] mr-[15px] text-[#162318]'>
              <Link href={{ pathname: '/findUser/id' }} style={{ textDecoration: 'none' }}>
                <div className='font-normal text-[#162318] cursor-pointer mobile:text-[14px]'>
                  아이디 찾기
                </div>
              </Link>
              <div>|</div>
              <Link href={{ pathname: '/findUser/password' }} style={{ textDecoration: 'none' }}>
                <div className='font-normal text-[#162318] cursor-pointer mobile:text-[14px]'>
                  비밀번호 찾기
                </div>
              </Link>
            </div>

            <div className='mt-[90px]'>
              <div
                className='flex items-center justify-center h-[64px] w-[520px] mobile:w-full px-[10px] py-[20px] rounded-[5px] mb-[15px]
                  bg-[#909090] border-[#909090] text-[#ffffff] text-center mobile:text-[14px] cursor-pointer'
                onClick={onClickLogin}
              >
                LOGIN
              </div>
              <div
                className='flex justify-center items-center h-[64px] rounded-[5px] bg-[#fee500] mb-[15px] cursor-pointer'
                onClick={handleLogin}
              >
                <img
                  src='/kakaoIcon.png'
                  alt='카카오 로그인 버튼'
                  className='w-[20px] h-[18px] mr-[5px]'
                />
                <div className='text-[16px] ml-10px mobile:text-[14px]'>
                  카카오로 3초 만에 시작하기
                </div>
              </div>
              <Link
                href={{ pathname: '/reservation/nonMember' }}
                style={{ textDecoration: 'none' }}
              >
                <div className='flex items-center justify-center h-[64px] text-[18px] rounded-[5px] bg-[#000000] text-[#ffffff] cursor-pointer  mobile:text-[14px] mobile:w-full'>
                  비회원 예약조회
                </div>
              </Link>
              <div className='flex items-center justify-center gap-[10px] mr-[15px] mt-[10px] text-[14px]'>
                <p className={'text-[#929292]'}>계정이 없으신가요? </p>
                <Link href={{ pathname: '/join' }} style={{ textDecoration: 'none' }}>
                  <div className='btn_find_account mobile:text-[14px]'>
                    <p className={'underline text-[#929292]'}>회원가입</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}